import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
    Typography,
    Box,
    Container,
    Grid,
    Paper,
    useTheme,
    styled,
    Button,
    useMediaQuery
} from '@mui/material';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import {
    CustomAppBar,
    EventCard,
    Loading,
    getPublishedEvents,
    eventStateEnum,
    EnhancedErrorMessage
} from '@virtual-cinema/client-lib';
import AudienceAppBar from '../common/app-bar';

const FloatingElement = styled(Box)(({ delay = 0 }) => ({
    position: 'absolute',
    width: '150px',
    height: '150px',
    background: 'radial-gradient(circle, rgba(255,255,255,0.1) 0%, rgba(255,255,255,0) 70%)',
    animation: 'float 15s infinite',
    animationDelay: `${delay}s`,
    '@keyframes float': {
        '0%, 100%': {
            transform: 'translateY(0) rotate(0deg)',
        },
        '50%': {
            transform: 'translateY(-100px) rotate(180deg)',
        },
    },
}));

const FeaturedEventBanner = styled(Box)(({ theme }) => ({
    position: 'relative',
    width: '100%',
    height: 'auto',
    aspectRatio: '16/9',
    backgroundSize: 'cover',
    backgroundPosition: 'center',
    display: 'flex',
    alignItems: 'flex-end',
    marginBottom: theme.spacing(6),
    borderRadius: theme.spacing(2),
    overflow: 'hidden',
    [theme.breakpoints.down('sm')]: {
        aspectRatio: '4/3',
        marginBottom: theme.spacing(4),
        borderRadius: theme.spacing(1),
    },
    '&::before': {
        content: '""',
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: 'linear-gradient(to top, rgba(0,0,0,0.9) 0%, rgba(0,0,0,0.5) 50%, rgba(0,0,0,0.2) 100%)',
    }
}));

function LandingPage() {
    const [eventsData, setEventsData] = useState([]);
    const [error, setError] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const navigate = useNavigate();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    useEffect(() => {
        const get_published_events = async () => {
            setIsLoading(true);
            try {
                const { data, error } = await getPublishedEvents();
                if (error) {
                    throw new Error(error.message || "Failed to fetch events");
                }
                setEventsData(data);
            } catch (err) {
                console.error("Error fetching events:", err);
                setError(err.message || "An error occurred while fetching events. Please try again.");
            } finally {
                setIsLoading(false);
            }
        };
        get_published_events();
    }, []);

    // Temporarly hardcoded to IWD 2025 event, eventually server will return this Id.
    const featuredEventId = "9d402e15-dae1-4a45-90ae-5797e919d13c";
    const featuredEvent = eventsData?.find(event => event.info.id === featuredEventId) ||
        eventsData?.find(event => event.info.state === eventStateEnum.Active) ||
        eventsData?.find(event => event.info.state === eventStateEnum.Published);

    const liveEvents = eventsData?.filter(event =>
        event.info.state === eventStateEnum.Active && event.info.id !== featuredEvent?.info.id
    ) || [];

    const publishedEvents = eventsData?.filter(event =>
        event.info.state === eventStateEnum.Published && event.info.id !== featuredEvent?.info.id
    ) || [];

    const NoEventsMessage = () => (
        <Paper
            elevation={3}
            sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                bgcolor: 'rgba(255,255,255,0.05)',
                backdropFilter: 'blur(10px)',
                color: 'white',
                borderRadius: '16px',
                maxWidth: '800px',
                width: { xs: 'calc(100% - 32px)', sm: 'auto' },
                m: 2,
                p: { xs: 4, sm: 6 },
                border: '1px solid rgba(255,255,255,0.1)',
                position: 'relative',
                overflow: 'hidden'
            }}
        >
            {/* Header Section */}
            <Box sx={{ mb: 4, position: 'relative', zIndex: 1, textAlign: 'center' }}>
                <Typography
                    variant="h3"
                    component="h3"
                    align="center"
                    gutterBottom
                    sx={{
                        fontSize: { xs: '2rem', sm: '2.5rem' },
                        fontWeight: 'bold',
                        background: 'linear-gradient(45deg, #FF8E53 30%, #FE6B8B 90%)',
                        backgroundClip: 'text',
                        WebkitBackgroundClip: 'text',
                        color: 'transparent',
                        mb: 2
                    }}
                >
                    Discover Indie Film Gems!
                </Typography>
                <Typography
                    variant="h6"
                    align="center"
                    sx={{ fontSize: '1.1rem', lineHeight: 1.6, color: 'rgba(255,255,255,0.9)', maxWidth: '600px', mx: 'auto' }}
                >
                    We're crafting a unique platform for independent films. Sign up now to be the first to know when we launch!
                </Typography>
            </Box>

            {/* CTA Button */}
            <Button
                variant="contained"
                color="primary"
                href="/signup"
                sx={{
                    textTransform: 'none',
                    fontSize: '1.1rem',
                    fontWeight: 'bold',
                    px: 6,
                    py: 1.8,
                    mb: 5,
                    borderRadius: '50px',
                    background: 'linear-gradient(45deg, #FF8E53 30%, #FE6B8B 90%)',
                    boxShadow: '0 3px 15px rgba(255,142,83,0.3)',
                    '&:hover': {
                        background: 'linear-gradient(45deg, #FE6B8B 30%, #FF8E53 90%)',
                        transform: 'translateY(-2px)',
                        transition: 'all 0.3s ease'
                    }
                }}
            >
                Sign Up for Updates
            </Button>

            {/* Features Section */}
            <Grid container spacing={3} sx={{ textAlign: 'center', mb: 4 }}>
                <Grid item xs={12} sm={4}>
                    <Box sx={{ p: 2 }}>
                        <Typography variant="h4" sx={{ mb: 1 }}>🎬</Typography>
                        <Typography variant="h6" sx={{ mb: 1, color: 'primary.main' }}>
                            Exclusive Access
                        </Typography>
                        <Typography variant="body1" sx={{ color: 'rgba(255,255,255,0.7)' }}>
                            Be first to watch premieres
                        </Typography>
                    </Box>
                </Grid>
                <Grid item xs={12} sm={4}>
                    <Box sx={{ p: 2 }}>
                        <Typography variant="h4" sx={{ mb: 1 }}>💫</Typography>
                        <Typography variant="h6" sx={{ mb: 1, color: 'primary.main' }}>
                            Support Artists
                        </Typography>
                        <Typography variant="body1" sx={{ color: 'rgba(255,255,255,0.7)' }}>
                            Help indie filmmakers thrive
                        </Typography>
                    </Box>
                </Grid>
                <Grid item xs={12} sm={4}>
                    <Box sx={{ p: 2 }}>
                        <Typography variant="h4" sx={{ mb: 1 }}>🎁</Typography>
                        <Typography variant="h6" sx={{ mb: 1, color: 'primary.main' }}>
                            Special Perks
                        </Typography>
                        <Typography variant="body1" sx={{ color: 'rgba(255,255,255,0.7)' }}>
                            Win tickets and merchandise
                        </Typography>
                    </Box>
                </Grid>
            </Grid>

            {/* Footer Message */}
            <Typography
                variant="h6"
                align="center"
                sx={{
                    fontSize: '1.2rem',
                    color: 'rgba(255,255,255,0.9)',
                    mt: 2
                }}
            >
                Stay tuned for the magic! ✨
            </Typography>
        </Paper>
    );


    const renderFeaturedEvent = (event) => {
        const details = event ? JSON.parse(event.info.details) : null;

        const handleBannerClick = (e) => {
            // Only navigate if the click wasn't on the button
            if (!e.target.closest('button')) {
                navigate(`/event/${event.info.id}`);
            }
        };

        return event && (
            <Box sx={{ width: '100%' }}>
                <Typography
                    variant="h4"
                    component="h2"
                    sx={{
                        fontWeight: 'bold',
                        mb: 2,
                        color: 'white',
                        fontSize: { xs: '1.5rem', sm: '2rem' },
                        pl: { xs: 2, sm: 3, md: 4 }
                    }}
                >
                    Featured Event
                </Typography>
                <FeaturedEventBanner
                    onClick={handleBannerClick}
                    sx={{
                        backgroundImage: `url(${event.assets[0]?.bannerUrl})`,
                    }}
                >
                    <Box
                        sx={{
                            position: 'relative',
                            p: { xs: 3, sm: 4 },
                            width: '100%',
                            display: 'flex',
                            flexDirection: 'column',
                            gap: { xs: 1, sm: 1.5 },
                            background: 'linear-gradient(to top, rgba(0,0,0,0.8) 0%, rgba(0,0,0,0.4) 100%)',
                        }}
                    >
                        <Typography
                            variant="overline"
                            sx={{
                                color: theme.palette.primary.main,
                                fontWeight: 'bold',
                                fontSize: { xs: '0.75rem', sm: '0.875rem' }
                            }}
                        >
                            {event.info.state === eventStateEnum.Active ? 'LIVE NOW' : 'FEATURED EVENT'}
                        </Typography>
                        <Typography
                            variant="h3"
                            sx={{
                                color: 'white',
                                fontWeight: 'bold',
                                fontSize: { xs: '1.5rem', sm: '2rem', md: '2.5rem' },
                                lineHeight: 1.2,
                                textShadow: '2px 2px 4px rgba(0,0,0,0.5)'
                            }}
                        >
                            {event.info.name}
                        </Typography>
                        {details?.summary && (
                            <Typography
                                variant="body1"
                                sx={{
                                    color: 'rgba(255,255,255,0.9)',
                                    maxWidth: '600px',
                                    fontSize: { xs: '0.875rem', sm: '1rem' },
                                    mb: { xs: 1, sm: 2 },
                                    display: '-webkit-box',
                                    WebkitLineClamp: { xs: 2, sm: 3 },
                                    WebkitBoxOrient: 'vertical',
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    textShadow: '1px 1px 2px rgba(0,0,0,0.5)'
                                }}
                            >
                                {details.summary}
                            </Typography>
                        )}
                        <Button
                            variant="contained"
                            size={isMobile ? "medium" : "large"}
                            startIcon={<PlayArrowIcon />}
                            onClick={(e) => {
                                e.stopPropagation(); // Prevent banner click
                                navigate(`/event/${event.info.id}`);
                            }}
                            sx={{
                                px: { xs: 3, sm: 4 },
                                py: { xs: 1, sm: 1.5 },
                                fontSize: { xs: '0.9rem', sm: '1.1rem' },
                                alignSelf: 'flex-start',
                                mt: { xs: 1, sm: 0 }
                            }}
                        >
                            {event.info.state === eventStateEnum.Active ? 'Watch Now' : 'Learn More'}
                        </Button>
                    </Box>
                </FeaturedEventBanner>
            </Box>
        );
    };

    const renderEventsSection = (title, events) => {
        const displayEvents = events.filter(event => event.info.id !== featuredEventId);

        return displayEvents.length > 0 && (
            <Box sx={{ mb: 4, width: '100%' }}>
                <Typography
                    variant="h5"
                    component="h2"
                    sx={{
                        fontWeight: 'bold',
                        mb: 3,
                        color: 'white',
                        pl: { xs: 2, sm: 3, md: 4 }
                    }}
                >
                    {title}
                </Typography>
                <Grid container spacing={3} sx={{ px: { xs: 1, sm: 2, md: 3 } }}>
                    {displayEvents.map((event) => (
                        <Grid
                            item
                            key={event.info.id}
                            xs={12}
                            sm={6}
                            md={4}
                            lg={3}
                        >
                            <EventCard
                                event={event}
                                onClick={() => navigate(`/event/${event.info.id}`)}
                            />
                        </Grid>
                    ))}
                </Grid>
            </Box>
        );
    };

    return (
        <Box
            sx={{
                position: 'relative',
                minHeight: '100vh',
                display: 'flex',
                flexDirection: 'column',
                background: 'linear-gradient(rgba(0,0,0,0.7), rgba(0,0,0,0.8))',
                overflow: 'hidden',
                color: 'white'
            }}
        >
            {[...Array(5)].map((_, i) => (
                <FloatingElement
                    key={i}
                    delay={i * 1.5}
                    sx={{
                        left: `${i * 25}%`,
                        top: `${Math.random() * 100}%`,
                    }}
                />
            ))}

            <Container maxWidth="xl">
                <AudienceAppBar />
            </Container>

            <Container
                maxWidth="xl"
                sx={{
                    mt: { xs: 2, sm: 4 },
                    px: { xs: 1, sm: 2, md: 3 },
                    flex: 1,
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center'
                }}
            >
                {error ? (
                    <EnhancedErrorMessage message={error} />
                ) : isLoading ? (
                    <Loading />
                ) : (
                    <>
                        {renderFeaturedEvent(featuredEvent)}
                        {renderEventsSection("Live Events", liveEvents)}
                        {renderEventsSection("Upcoming Events", publishedEvents)}
                        {eventsData.length === 0 && <NoEventsMessage />}
                    </>
                )}
            </Container>
        </Box>
    );
}

export default LandingPage;